import { useState, useRef } from "react";
import { X, Star, Image, Video } from "lucide-react";
import { toast } from "sonner";
import { createPost } from "../../lib/api";
import API_BASE_URL from "../../config";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const POST_TYPES = [
  { value: "experience", label: "Experience" },
  { value: "review", label: "Review" },
  { value: "tip", label: "Tip" },
  { value: "question", label: "Question" },
];

const MAX_PHOTOS = 4;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB

export default function CreatePostModal({ channelId, places, onClose, onCreated }) {
  const [form, setForm] = useState({ type: "experience", title: "", content: "", rating: 5, place_id: "" });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // ✅ Single clean uploadFile function
  const uploadFile = async (file) => {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API}/upload`, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Upload failed");
    }
    const data = await res.json();
    const url = data.url?.startsWith("http")
      ? data.url
      : `${API_BASE_URL}${data.url || `/api/files/${data.path}`}`;
    return url;
  };

  const handlePhotoAdd = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentPhotos = mediaFiles.filter(m => m.type === "image").length;
    const available = MAX_PHOTOS - currentPhotos;

    if (available <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      e.target.value = "";
      return;
    }

    const toUpload = files.slice(0, available);
    if (files.length > available) {
      toast.warning(`Only ${available} more photo(s) can be added.`);
    }

    setUploading(true);
    for (let i = 0; i < toUpload.length; i++) {
      const file = toUpload[i];
      if (file.size > MAX_PHOTO_SIZE) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }
      setUploadProgress(`Uploading photo ${i + 1}/${toUpload.length}...`);
      try {
        const url = await uploadFile(file);
        setMediaFiles(prev => [...prev, { url, type: "image", name: file.name }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    setUploadProgress("");
    e.target.value = "";
  };

  const handleVideoAdd = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const hasVideo = mediaFiles.some(m => m.type === "video");
    if (hasVideo) {
      toast.error("Only 1 video allowed per post");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      toast.error("Video must be under 50MB");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setUploadProgress("Uploading video...");
    try {
      const url = await uploadFile(file);
      setMediaFiles(prev => [...prev, { url, type: "video", name: file.name }]);
      toast.success("Video uploaded!");
    } catch {
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
      setUploadProgress("");
      e.target.value = "";
    }
  };

  const removeMedia = (idx) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.content.trim().length < 50) {
      setError("Content must be at least 50 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        channel_id: channelId,
        media_urls: mediaFiles.map(m => m.url),
        tags: [],
      };
      if (!payload.place_id) payload.place_id = null;
      if (form.type !== "review") delete payload.rating;
      await createPost(payload);
      toast.success("Post created!");
      onCreated?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create post");
    } finally {
      setLoading(false);
    }
  };

  const photoCount = mediaFiles.filter(m => m.type === "image").length;
  const hasVideo = mediaFiles.some(m => m.type === "video");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="create-post-modal">
      <div className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#0B1B13]">Create Post</h2>
          <button onClick={onClose} className="text-[#5C6B64] hover:text-[#0B1B13]" data-testid="close-create-post">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="create-post-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Post Type */}
          <div>
            <label className="block text-sm font-medium text-[#2D3B34] mb-1.5">Post Type</label>
            <div className="flex gap-2 flex-wrap">
              {POST_TYPES.map((t) => (
                <button key={t.value} type="button"
                  onClick={() => setForm({ ...form, type: t.value })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.type === t.value
                      ? "bg-[#123D2B] text-white border-[#123D2B]"
                      : "bg-white border-[#E2E8E4] text-[#5C6B64] hover:border-[#3FAF7A]"
                  }`}
                  data-testid={`type-${t.value}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Place */}
          <div>
            <label className="block text-sm font-medium text-[#2D3B34] mb-1.5">Place (optional)</label>
            <select value={form.place_id}
              onChange={(e) => setForm({ ...form, place_id: e.target.value })}
              className="w-full border border-[#E2E8E4] rounded-lg px-4 py-2.5 bg-[#F7FAF8] focus:bg-white focus:ring-2 focus:ring-[#3FAF7A]/50 focus:border-[#3FAF7A] outline-none transition-all text-sm"
              data-testid="post-place-select">
              <option value="">No specific place</option>
              {places.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Media Upload */}
          <div>
            <label className="block text-sm font-medium text-[#2D3B34] mb-2">
              Photos & Video
              <span className="text-[#5C6B64] font-normal ml-1">(max {MAX_PHOTOS} photos, 1 video)</span>
            </label>

            {/* Previews */}
            {mediaFiles.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {mediaFiles.map((media, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden border border-[#E2E8E4] bg-[#F7FAF8]">
                    {media.type === "image" ? (
                      <img src={media.url} alt={media.name} className="w-full h-24 object-cover" />
                    ) : (
                      <video src={media.url} className="w-full h-24 object-cover" />
                    )}
                    <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                      media.type === "image" ? "bg-[#3FAF7A]/80 text-white" : "bg-[#3B82F6]/80 text-white"
                    }`}>
                      {media.type === "image" ? "Photo" : "Video"}
                    </div>
                    <button type="button" onClick={() => removeMedia(idx)}
                      className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors">
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="mb-2 p-2.5 bg-[#3FAF7A]/10 border border-[#3FAF7A]/20 rounded-lg flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-[#3FAF7A] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-[#3FAF7A] font-medium">{uploadProgress}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-2">
              {photoCount < MAX_PHOTOS && (
                <button type="button" onClick={() => photoInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 border-2 border-dashed border-[#E2E8E4] hover:border-[#3FAF7A] rounded-xl py-3 flex items-center justify-center gap-2 text-sm text-[#5C6B64] hover:text-[#3FAF7A] transition-colors disabled:opacity-50">
                  <Image className="w-4 h-4" strokeWidth={1.5} />
                  Add Photo {photoCount > 0 && `(${photoCount}/${MAX_PHOTOS})`}
                </button>
              )}
              {!hasVideo && (
                <button type="button" onClick={() => videoInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1 border-2 border-dashed border-[#E2E8E4] hover:border-[#3B82F6] rounded-xl py-3 flex items-center justify-center gap-2 text-sm text-[#5C6B64] hover:text-[#3B82F6] transition-colors disabled:opacity-50">
                  <Video className="w-4 h-4" strokeWidth={1.5} />
                  Add Video
                </button>
              )}
            </div>

            <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoAdd} />
            <input ref={videoInputRef} type="file" accept="video/mp4,video/mov,video/quicktime" className="hidden" onChange={handleVideoAdd} />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2D3B34] mb-1.5">Title</label>
            <input required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-[#E2E8E4] rounded-lg px-4 py-2.5 bg-[#F7FAF8] focus:bg-white focus:ring-2 focus:ring-[#3FAF7A]/50 focus:border-[#3FAF7A] outline-none transition-all text-sm"
              placeholder="Give your post a title"
              data-testid="post-title-input" />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-[#2D3B34] mb-1.5">
              Content <span className="text-[#5C6B64] font-normal">(min 50 chars)</span>
            </label>
            <textarea required value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={5}
              className="w-full border border-[#E2E8E4] rounded-lg px-4 py-2.5 bg-[#F7FAF8] focus:bg-white focus:ring-2 focus:ring-[#3FAF7A]/50 focus:border-[#3FAF7A] outline-none transition-all text-sm resize-none"
              placeholder="Share your thoughts... (at least 50 characters)"
              data-testid="post-content-input" />
            <p className={`text-xs mt-1 ${form.content.length >= 50 ? "text-[#3FAF7A]" : "text-[#5C6B64]"}`}>
              {form.content.length}/50 characters {form.content.length >= 50 && "✓"}
            </p>
          </div>

          {/* Rating */}
          {form.type === "review" && (
            <div>
              <label className="block text-sm font-medium text-[#2D3B34] mb-1.5">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    data-testid={`rating-${n}`}>
                    <Star className={`w-6 h-6 transition-colors ${
                      n <= form.rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-[#E2E8E4]"
                    }`} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border-2 border-[#E2E8E4] text-[#5C6B64] hover:bg-[#F7FAF8] rounded-full py-2.5 font-medium transition-colors text-sm">
              Cancel
            </button>
            <button type="submit" disabled={loading || uploading}
              className="flex-1 bg-[#123D2B] text-white hover:bg-[#0B1B13] rounded-full py-2.5 font-medium transition-colors disabled:opacity-50 text-sm"
              data-testid="submit-post">
              {loading ? "Posting..." : uploading ? "Uploading..." : "Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}